"use client";
import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect } from "react";
import { Theme, THEMES } from "../utils/constants";

export interface ThemeState {
    type: Theme;
    showRebrandTheme?: boolean;
    devToolsOpen?: boolean;
}

interface ThemeContextInterface {
    theme: ThemeState;
    updateTheme: (stateUpdate: Partial<ThemeState>) => void;
}

interface ThemeProviderProps {
    children: ReactNode;
    initialTheme?: Theme;
    showRebrandTheme?: boolean;
}

export const ThemeContext = createContext<ThemeContextInterface | undefined>(undefined);

function ThemeProvider({ children, showRebrandTheme, initialTheme }: ThemeProviderProps) {
    const [theme, setTheme] = useState<ThemeState>({
        type: initialTheme ?? THEMES.DEFAULT,
        showRebrandTheme: !!showRebrandTheme,
        devToolsOpen: true
    });

    const updateTheme = useCallback((stateUpdate: Partial<ThemeState>) => {
        setTheme(prevState => {
            return { ...prevState, ...stateUpdate };
        });
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme.type);
    }, [theme.type]);

    const value = useMemo(() => ({ theme, updateTheme }), [theme, updateTheme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function useThemeContext() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeContext must be used within an ThemeProvider");
    }
    return context;
}

export { ThemeProvider, useThemeContext };
